import { useState, useEffect, useRef, Suspense } from "react";
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  IoPersonOutline as PersonOutline,
  IoLogOutOutline as LogOutOutline,
  IoHelpBuoyOutline as HelpOutline,
} from 'react-icons/io5';
import LangSelector from './LangSelector';
import { toast } from 'react-toastify';
import { BlockedModal as LoginModal } from "../modals/BlockedModal";
import { SUPPORT } from '../../settings';
// `flag()` rather than an import: CONTACT may not be declared at all in a
// deployment that has no contact page, and webpack fails the build on a
// static import of a missing export (see common/tools/featureFlag).
import { flag } from '../tools/featureFlag';
import logo from '../../img/brand/mark.svg';
import { sidebarSlots } from '../shell/registry';
import { useShell } from '../shell/ShellContext';

export const MenuLeft = (props) => {
  const { t } = useTranslation('layout');
  const navigate = useNavigate();
  const { LoginComponent, userService } = useShell();
  const [ expanded, setExpanded ] = useState(false);
  const [ loginModalShow, setLoginModalShow ] = useState(false);

  // Module-contributed sidebar snippets, discovered from each module's
  // shell.js (see common/shell/registry.js). Inline widgets share one section;
  // `section: true` snippets (collapsible groups) get their own.
  //
  // Each is rendered inside its own Suspense boundary with a `null` fallback. A
  // slot is the first thing to ask for its module's i18n namespace, and
  // react-i18next suspends until that namespace lands; unheld, the suspension
  // reached the root boundary and blanked the whole page once per module. A
  // widget that is not ready yet should simply not be there yet — it is a
  // sidebar entry, not the page.
  const enabledSlots = sidebarSlots.filter((slot) => slot.enabled());
  const inlineSlots = enabledSlots.filter((slot) => !slot.section);
  const sectionSlots = enabledSlots.filter((slot) => slot.section);

  // Collapse the labels when the viewport actually narrows past the breakpoint —
  // and only then. A phone fires `resize` while you scroll (the address bar
  // collapses, the height changes, the width does not), so reacting to every
  // resize meant scrolling with the drawer open faded every label out while the
  // drawer stayed put. The drawer being open also outranks this entirely: it is
  // the full-width menu, so it must keep its labels whatever the viewport does.
  const lastWidth = useRef(typeof window !== 'undefined' ? window.innerWidth : 0);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const changed = width !== lastWidth.current;
      lastWidth.current = width;
      if (changed && width < 768 && !props.mobileOpen) {
        setExpanded(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [props.mobileOpen]);

  useEffect(() => {
    if (props.mobileOpen) {
      setExpanded(true);
    }
  }, [props.mobileOpen]);

  // Open the login modal when any action needs an authenticated session but the
  // visitor is anonymous (e.g. clicking "pay" on the plans page). Mirrors the
  // Header's handling so the prompt works on pages that render only the sidebar.
  useEffect(() => {
    const handleDisconnected = () => setLoginModalShow(true);
    window.addEventListener("disconnected", handleDisconnected);
    return () => window.removeEventListener("disconnected", handleDisconnected);
  }, []);

  const logOut = () => {
    userService.logout().then(
      (response) => {
        if (response.detail[0].success) {
          toast.success(t(response.detail[0].success));
        } else {
          toast.error(t(response.detail[0].error));
        }
      }
    );
    navigate("/home");
    props.setUser(false);
    localStorage.removeItem("user");
  };

  const handleLogin = () => {
    setLoginModalShow(true);
  };

  return (
    <>
      <div className={`sidebar-container ${expanded ? 'expanded' : ''} ${props.mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <img src={logo} alt="Migratis" className="sidebar-logo" />
          <span className="sidebar-header-title">Migratis</span>
        </div>

        <div className="sidebar-content">
          {props.user ? (
            <>
              {inlineSlots.length > 0 && (
                <div className="sidebar-section">
                  {inlineSlots.map((slot) => (
                    <Suspense fallback={null} key={slot.id}>
                      <slot.Component
                        user={props.user}
                        onMobileClose={props.onMobileClose}
                      />
                    </Suspense>
                  ))}
                </div>
              )}

              {sectionSlots.map((slot) => (
                <div className="sidebar-section" key={slot.id}>
                  <Suspense fallback={null}>
                    <slot.Component
                      user={props.user}
                      onMobileClose={props.onMobileClose}
                    />
                  </Suspense>
                </div>
              ))}

              <div className="sidebar-divider" />

              <div className="sidebar-section">
                <NavLink
                  to="/account"
                  className={({isActive}) => `sidebar-item ${isActive ? 'active' : ''}`}
                  onClick={props.onMobileClose}
                >
                  <PersonOutline />
                  <span className="sidebar-label">{t('account-settings')}</span>
                </NavLink>

                {/* No globe icon: the selected flag *is* this row's icon. A
                    generic globe next to it said nothing the flag did not. */}
                <div className="sidebar-item sidebar-item-lang">
                  <LangSelector compact={false} />
                </div>

                {/* Whichever channel exists. Falling through to /contact
                    whenever SUPPORT was off emitted a link to a route the base
                    template gates behind its own CONTACT flag — a dead menu
                    entry on every install with no support module. */}
                {SUPPORT ? (
                  <a href="/support/ticket" className="sidebar-item" onClick={props.onMobileClose}>
                    <HelpOutline />
                    <span className="sidebar-label">{t('support')}</span>
                  </a>
                ) : flag('CONTACT') ? (
                  <a href="/contact" className="sidebar-item" onClick={props.onMobileClose}>
                    <HelpOutline />
                    <span className="sidebar-label">{t('contact')}</span>
                  </a>
                ) : null}

                <div className="sidebar-item" onClick={() => { logOut(); props.onMobileClose(); }}>
                  <LogOutOutline />
                  <span className="sidebar-label">{t('logout')}</span>
                </div>
              </div>
            </>
          ) : (
            <div className="sidebar-section">
              <div className="sidebar-item" onClick={() => { handleLogin(); props.onMobileClose(); }}>
                <PersonOutline />
                <span className="sidebar-label">{t('login')}</span>
              </div>

              <div className="sidebar-item sidebar-item-lang">
                <LangSelector compact={false} />
              </div>

              {flag('CONTACT') && (
                <a href="/contact" className="sidebar-item" onClick={props.onMobileClose}>
                  <HelpOutline />
                  <span className="sidebar-label">{t('contact')}</span>
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      <LoginModal
        show={loginModalShow}
        onHide={() => setLoginModalShow(false)}
        title={t('login')}
      >
        <LoginComponent
          setUser={props.setUser}
          setLoginModalShow={setLoginModalShow}
        />
      </LoginModal>
    </>
  );
};
